import React, { ReactNode, useState } from "react";
import styled from "styled-components";
import { Item } from "@charty/legend/item/model";
import { Serie } from "@charty/models";
import { AbstractDate, Range } from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import Chart from "./chart";
import { useLegendSeries, useSortSeries } from "./data";
import Legend from "./legend";

export type Sort = "alpha" | "mainValue" | "default";

export type ChartTooltip = {
  count: number;
  label: string;
  color: string;
  date: AbstractDate;
  total?: number;
};

export interface IProps {
  series: Serie[];
  range: Range;
  locale: Locale;
  onClickBar?: (
    uid: Item["id"],
    range: { from: AbstractDate; to: AbstractDate }
  ) => void;
  onClickLegend?: (uid: Item["id"]) => void;
  tooltip?: (serie: Item, total?: number) => ReactNode;
  total?: number;
  className?: string;
  chartTooltip?: (props: ChartTooltip[]) => ReactNode;
  sort?: Sort;
  isNormalized?: boolean;
  scale?: "logarithmic" | "linear";
  timezone: Timezone;
  disableAnimation?: boolean;
  isFullscreen?: boolean;
  metricLabel?: string;
  displayVolumes?: boolean;
}

const Root = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const StackedBarChart = ({
  series,
  range,
  locale,
  tooltip,
  total,
  onClickBar,
  onClickLegend,
  className,
  chartTooltip,
  sort = "mainValue",
  isNormalized,
  scale = "linear",
  timezone,
  disableAnimation = false,
  isFullscreen,
  metricLabel,
  displayVolumes,
}: IProps) => {
  const [hovered, setHovered] = useState<string>(null);
  const sortedSeries = useSortSeries(sort, series);
  const legendSeries = useLegendSeries(sortedSeries);
  return (
    <Root className={className}>
      <Legend
        series={legendSeries}
        setHovered={setHovered}
        locale={locale}
        onClickLegend={onClickLegend}
        tooltip={tooltip}
        total={total}
        range={range}
        disableAnimation={disableAnimation}
        displayVolumes={displayVolumes}
      />
      <Chart
        hovered={hovered}
        chartTooltip={chartTooltip}
        onClickBar={onClickBar}
        locale={locale}
        range={range}
        series={sortedSeries}
        scale={scale || "linear"}
        legendSeries={legendSeries}
        isNormalized={isNormalized}
        timezone={timezone}
        disableAnimation={disableAnimation}
        isFullscreen={isFullscreen}
        metricLabel={metricLabel}
      />
    </Root>
  );
};

export default StackedBarChart;
